import React from 'react';
import PropTypes from 'prop-types';
import { injectIntl, FormattedMessage } from 'react-intl';
import { Field, ErrorMessage, getIn } from 'formik';
import InputErrorMessage from './InputErrorMessage';
import formStyles from '../../styles/forms.module.scss';

import s from './TextInput.module.scss';

/** Renders a simple text input with associated label */
function TextInput(props) {
  const {
    field,
    labelMsg,
    helpMsg,
    labelPosition,
    labelClass,
    inline,
    fullWidth,
    boldLabel,
    unit,
    required,
    disabled,
    fieldType,
    autoComplete,
  } = props;

  const { value, name, onChange } = field;

  const classNames = [s.textInput];

  if (!inline) {
    if (labelPosition === 'top') {
      classNames.push(s.labelTop);
    }
    if (labelPosition === 'left') {
      classNames.push(s.labelLeft);
    }
  }

  if (labelClass) {
    classNames.push(labelClass);
  }

  if (fullWidth) {
    classNames.push(s.fullWidth);
  }

  if (boldLabel) {
    classNames.push(s.bold);
  }
  const className = classNames.join(' ');

  let isError = false;
  const {
    form: { touched },
  } = props;
  const {
    form: { errors },
  } = props;
  const touchedTemp = getIn(touched, name);
  const errorsTemp = getIn(errors, name);
  if (touchedTemp && errorsTemp) {
    isError = true;
  }

  return (
    <label
      htmlFor={name}
      className={`${className} ${isError ? formStyles.errorBorder : ''}`}
    >
      {labelMsg && (
        <span
          className={`${s.labelSpan} ${isError ? formStyles.errorMessage : ''}`}
        >
          <FormattedMessage id={labelMsg} />
          {required && <span>*</span>}
        </span>
      )}
      {helpMsg && (
        <div className={s.helpMsg}>
          <FormattedMessage id={helpMsg} />
        </div>
      )}
      <div className={s.inputUnitWrapper}>
        <Field
          type={fieldType}
          id={name}
          name={name}
          value={value || ''}
          onChange={onChange}
          required={required}
          disabled={disabled}
          autoComplete={autoComplete}
          aria-invalid={isError}
        />
        {unit && <div className={s.unit}>{unit}</div>}
      </div>
      {labelPosition === 'left' && <div className={s.flexBreak} />}
      <ErrorMessage name={name} component={InputErrorMessage} />
    </label>
  );
}

TextInput.defaultProps = {
  labelMsg: null,
  helpMsg: '',
  labelPosition: 'top',
  labelClass: '',
  inline: false,
  fullWidth: false,
  boldLabel: false,
  unit: null,
  required: false,
  disabled: false,
  fieldType: 'text',
  autoComplete: 'on',
};

TextInput.propTypes = {
  /** Formik form */
  form: PropTypes.shape({
    // eslint-disable-next-line react/forbid-prop-types
    touched: PropTypes.object,
    // eslint-disable-next-line react/forbid-prop-types
    errors: PropTypes.object,
  }).isRequired,
  /** Formik field object */
  field: PropTypes.shape({
    onChange: PropTypes.func.isRequired,
    value: PropTypes.string,
    name: PropTypes.string.isRequired,
  }).isRequired,
  /** Text for the input label */
  labelMsg: PropTypes.string,
  /** Help text displayed between label and input */
  helpMsg: PropTypes.string,
  /** Set the position of the label in relation to the input */
  labelPosition: PropTypes.oneOf(['left', 'top']),
  /** Class to apply to the label element */
  labelClass: PropTypes.string,
  /** Styles the input inline uses no margin top */
  inline: PropTypes.bool,
  /** Set with to 100% */
  fullWidth: PropTypes.bool,
  /** Make the label bold  */
  boldLabel: PropTypes.bool,
  /** optional unit displayed after the input */
  unit: PropTypes.string,
  /** Set the autocomplete attribute of the field. Possible values according to https://www.w3.org/TR/html52/sec-forms.html#sec-autofill */
  autoComplete: PropTypes.string,
  required: PropTypes.bool,
  /** way to disable the field */
  disabled: PropTypes.bool,
  fieldType: PropTypes.string,
};

export default injectIntl(TextInput);
